import { Check } from "@mui/icons-material";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { Profile } from "@readyfit-common/models";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import AthleteIcon from "src/grading/components/AthleteIcon/AthleteIcon";
import { addBreadcrumb } from "src/store/reducers/navigation/navigationSlice";
import { ReportUser } from "src/store/reducers/reporting/reportingApiSlice";
import { v4 } from "uuid";

const Row = ({
  data,
  userColumns,
  testColumns,
  exerciseColumns,
  trainingColumns,
  workoutColumns,
}: {
  data: ReportUser;
  userColumns: { id: string; label: string; checked: boolean }[];
  testColumns: { id: string; label: string; checked: boolean }[];
  exerciseColumns: { id: string; label: string; checked: boolean }[];
  trainingColumns: { id: string; label: string; checked: boolean }[];
  workoutColumns: { id: string; label: string; checked: boolean }[];
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const handleSeeProfile = async () => {
    dispatch(
      addBreadcrumb({
        label: "Athletes",
        path: "/grading/athletes",
      }),
    );
    history.push(`/grading/athletes/${data.id}`);
  };

  return (
    <>
      <TableRow onClick={handleSeeProfile} style={{ cursor: "pointer" }}>
        <TableCell>
          {(data.firstName || data.lastName) && (
            <AthleteIcon profile={data as Profile} />
          )}
        </TableCell>
        {userColumns.map((c, i) => {
          if (c.id.startsWith("u.")) {
            const key = c.id.replace("u.", "");

            if (key === "address") {
              const value = data.address
                ? `${data.address?.addressLine1} ${data.address?.addressLine2} ${data.address?.city} ${data.address?.state} ${data.address?.postalCode}`
                : "-";
              return (
                <TableCell key={c.id}>
                  {value.replaceAll(" ", "").length > 0 ? value : "-"}
                </TableCell>
              );
            }

            const value = (data as any)[key];

            if (!value && value !== 0)
              return <TableCell key={c.id}>-</TableCell>;

            if (["createdAt", "lastAppUse"].includes(key)) {
              return (
                <TableCell key={c.id}>
                  {new Date(Date.parse(value)).toLocaleDateString()}
                </TableCell>
              );
            }

            if (["activeTrainingPlanId", "lastTestId"].includes(key)) {
              return (
                <TableCell key={c.id}>
                  {value && <Check color="primary" />}
                </TableCell>
              );
            }

            return <TableCell key={c.id}>{value}</TableCell>;
          }

          if (c.id.startsWith("o.")) {
            const key = c.id.replace("o.", "");
            const value = (data.organization as any)[key];

            if (!value && value !== 0)
              return <TableCell key={c.id}>-</TableCell>;

            return <TableCell key={c.id}>{value}</TableCell>;
          }
        })}

        {testColumns.map((c) => {
          if (c.id.startsWith("t.") && data.lastTest) {
            const key = c.id.replace("t.", "");
            const value = (data.lastTest as any)[key];

            if (!value && value !== 0)
              return <TableCell key={c.id}>-</TableCell>;

            if (key === "date") {
              return (
                <TableCell key={c.id}>
                  {new Date(Date.parse(value)).toLocaleDateString()}
                </TableCell>
              );
            }

            return <TableCell key={c.id}>{value}</TableCell>;
          }

          return <TableCell key={c.id}> - </TableCell>;
        })}

        {exerciseColumns.map((c) => {
          if (c.id.startsWith("te.") && data.lastTest) {
            const key = c.id.replace("te.", "");
            const { exercises = [] } = data.lastTest;
            const values =
              exercises?.map((e) => {
                const value = (e as any)[key];
                if (!value && value !== 0) return "-";
                return value;
              }) || [];

            if (values.length === 0) return <TableCell key={c.id}>-</TableCell>;

            return (
              <TableCell key={c.id}>
                {values.map((v) => (
                  <Typography key={v4()}>{v}</Typography>
                ))}
              </TableCell>
            );
          }

          return <TableCell key={c.id}>-</TableCell>;
        })}

        {trainingColumns.map((c) => {
          if (c.id.startsWith("p.") && data.trainingPlan) {
            const key = c.id.replace("p.", "");
            const value = (data.trainingPlan as any)[key];

            if (!value && value !== 0)
              return <TableCell key={c.id}>-</TableCell>;

            return <TableCell key={c.id}>{value}</TableCell>;
          }

          if (c.id.startsWith("c.") && data.trainingPlan?.coach) {
            const key = c.id.replace("c.", "");
            const value = (data.trainingPlan.coach as any)[key];

            if (!value && value !== 0)
              return <TableCell key={c.id}>-</TableCell>;

            return <TableCell key={c.id}>{value}</TableCell>;
          }

          return <TableCell key={c.id}>-</TableCell>;
        })}

        {workoutColumns.map((c) => {
          if (c.id.startsWith("w.") && data.lastWorkout) {
            const key = c.id.replace("p.", "");
            const value = (data.trainingPlan as any)[key];

            if (!value && value !== 0)
              return <TableCell key={c.id}>-</TableCell>;

            if (key === "date")
              return (
                <TableCell key={c.id}>
                  {new Date(Date.parse(value)).toLocaleDateString()}
                </TableCell>
              );

            return <TableCell key={c.id}>{value}</TableCell>;
          }

          return <TableCell key={c.id}>-</TableCell>;
        })}
      </TableRow>
    </>
  );
};

const ReportingTable = ({
  columns,
  data,
  page,
  rowsPerPage,
  total,
  handleChangePage,
}: {
  columns: { id: string; label: string; checked: boolean }[];
  data: ReportUser[];
  page: number;
  rowsPerPage: number;
  total: number;
  handleChangePage: (event: unknown, newPage: number) => void;
}) => {
  const userColumns = columns.filter(
    (c) => (c.id.startsWith("u.") || c.id.startsWith("o.")) && c.checked,
  );

  const testColumns = columns.filter((c) => c.id.startsWith("t.") && c.checked);

  const exerciseColumns = columns.filter(
    (c) => c.id.startsWith("te.") && c.checked,
  );

  const trainingColumns = columns.filter(
    (c) => (c.id.startsWith("p.") || c.id.startsWith("c.")) && c.checked,
  );

  const workoutColumns = columns.filter(
    (c) => c.id.startsWith("w.") && c.checked,
  );

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableCell />
            {userColumns.map((c) => (
              <TableCell key={c.id}>{c.label}</TableCell>
            ))}
            {testColumns.map((c) => (
              <TableCell key={c.id}>{c.label}</TableCell>
            ))}
            {exerciseColumns.map((c) => (
              <TableCell key={c.id}>{c.label}</TableCell>
            ))}
            {trainingColumns.map((c) => (
              <TableCell key={c.id}>{c.label}</TableCell>
            ))}
            {workoutColumns.map((c) => (
              <TableCell key={c.id}>{c.label}</TableCell>
            ))}
          </TableHead>
          <TableBody>
            {data.map((d) => (
              <Row
                key={d.id}
                data={d}
                userColumns={userColumns}
                testColumns={testColumns}
                exerciseColumns={exerciseColumns}
                trainingColumns={trainingColumns}
                workoutColumns={workoutColumns}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </Paper>
  );
};

export default ReportingTable;
